import * as ko from "knockout";
import {ErrorManager} from "../../../error/ErrorManager";
import {ErrorObject} from "../../../error/ErrorObject";
import {nsUserWizard} from "./nsUserWizard";
import {PartialRecord} from "../../../index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {MD5} from "../../../util/md5";
import {AbstractModel} from "./AbstractModel";
import {IModelParams} from "./index";
import TWizardSyncfield = nsUserWizard.TWizardSyncfield;
import {validateEmail} from "../../../util/misc";

interface IMeta {
    [key: string]: KnockoutObservable<any>
    generateEmployeeNumber: KnockoutObservable<boolean | null>
    isEmployeeNumberChanged: KnockoutObservable<boolean>
}

const Attributes: TWizardSyncfield[] = [
    'EmployeeNumber',
    'GivenName',
    'Mobile',
    'PrivatePhone',
    'PrivateEmail',
    'Socialsecuritynumber',
    'Surname',
]

export class PersonaliaModel extends AbstractModel {

    public override readonly Attributes = Attributes
    public override readonly attr: PartialRecord<TWizardSyncfield, (KnockoutObservable<any> | KnockoutComputed<any>)> = {
        GivenName: ko.observable(''),
        Surname: ko.observable(''),
        Socialsecuritynumber: ko.observable(''),
        EmployeeNumber: ko.observable(''),
        Mobile: ko.observable(''),
        PrivatePhone: ko.observable(''),
        PrivateEmail: ko.observable(''),
    }
    public override readonly meta: IMeta = {
        generateEmployeeNumber: ko.observable(null),
        isEmployeeNumberChanged: ko.observable(false),
    }
    public override readonly errorManager: ErrorManager = new ErrorManager()
    protected readonly eo: Partial<Record<TWizardSyncfield, ErrorObject>> = {}
    protected readonly eoSsnInUse: ErrorObject = new ErrorObject()
    protected readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()

    constructor(params: IModelParams) {
        super(params)

        this.Attributes.forEach(key => {
            this.eo[key] = new ErrorObject({name: key})
            this.errorManager.addErrorObject(this.eo[key])
        })

        this.derived.DisplayName = ko.pureComputed(() => {
            return [this.attr.GivenName?.(), this.attr.Surname?.()].filter(Boolean).join(' ')
        })

        const ssn = this.attr.Socialsecuritynumber
        const employeeNumber = this.attr.EmployeeNumber
        if (ssn) {
            this.kosub.subscribe(ssn, () => {
                if (this.meta.generateEmployeeNumber()) {
                    employeeNumber?.(MD5(ssn()));
                }
            })
        }

        this.kosub.subscribe(this.meta.generateEmployeeNumber, bool => {
            if (bool) {
                employeeNumber?.(MD5(ssn?.() ?? ''));
            } else {
                employeeNumber?.(this.getOriginalValue('EmployeeNumber'));
            }
        })
    }

    public initErrorObjects() {
        this.eo.GivenName?.addValidator({
            fn: () => !!this.attr.GivenName?.(),
            message: ``,
        })
        this.eo.Surname?.addValidator({
            fn: () => !!this.attr.Surname?.(),
            message: ``,
        })
        this.eo.Socialsecuritynumber?.addValidator({
            fn: () => !!this.attr.Socialsecuritynumber?.(),
            message: ``,
        })
        this.eo.EmployeeNumber?.addValidator({
            fn: () => !!this.attr.EmployeeNumber?.(),
            message: ``,
        })
        this.eo.EmployeeNumber?.addValidator({
            fn: () => !this.dataset.usedValues?.EmployeeNumber.includes(this.attr.EmployeeNumber?.() ?? ''),
            message: `already in use`,
            prerequisite: () => !!this.attr.EmployeeNumber?.(),
        })
        this.eo.EmployeeNumber?.addValidator({
            fn: () => !this.dataset.usedValues?.SourceId.includes(this.derived.SourceId?.() ?? ''),
            message: `already in use`,
            prerequisite: () => !!this.derived.SourceId?.(),
        })

        // just a warning
        this.eoSsnInUse.addValidator({
            fn: () => !this.dataset.usedValues?.Socialsecuritynumber.includes(this.attr.Socialsecuritynumber?.() ?? ''),
            message: `already in use`,
            prerequisite: () => !!this.attr.Socialsecuritynumber?.(),
        })

        this.eo.PrivateEmail?.addValidator({
            fn: () => validateEmail(this.attr.PrivateEmail?.() ?? ''),
            message: `Invalid email`,
            prerequisite: () => !!this.attr.PrivateEmail?.(),
        })
    }

    dispose() {
        this.kosub.unsuscribeAll()
    }
}